import {
  cloneElement,
  FC,
  isValidElement,
  PropsWithChildren,
  useState,
} from 'react';
import {
  Tooltip as MUITooltip,
  TooltipProps as MUITooltipProps,
} from '@mui/material';
import cn from 'classnames';

type TextTooltipProps = PropsWithChildren<{
  value: string;
  offsetX?: number;
  offsetY?: number;
  placement?: MUITooltipProps['placement'];
  autoShow?: boolean;
  classNames?: {
    tooltipClassName?: string;
    titleClassName?: string;
    childrenClassName?: string;
  };
}>;

const TEXT_OVERFLOW_CLASSNAME =
  'block whitespace-nowrap overflow-hidden text-ellipsis';

export const TextTooltip: FC<TextTooltipProps> = ({
  value,
  offsetX = 0,
  offsetY = 0,
  placement,
  autoShow = true,
  classNames,
  children,
}) => {
  const [isOverflowed, setOverflowed] = useState(false);
  const showTooltip = autoShow ? isOverflowed : true;

  const onElementChange = (elem: HTMLElement | null) => {
    if (!autoShow || !elem) {
      return;
    }

    const overflow =
      elem.offsetWidth < elem.scrollWidth ||
      elem.offsetHeight < elem.scrollHeight;

    setOverflowed(overflow);
  };

  const clonedChildren = isValidElement(children) ? (
    cloneElement<any>(children, {
      ...children.props,
      ref: onElementChange,
      className: cn(
        TEXT_OVERFLOW_CLASSNAME,
        children.props.className,
        classNames?.childrenClassName
      ),
    })
  ) : (
    <span
      ref={onElementChange}
      className={cn(TEXT_OVERFLOW_CLASSNAME, classNames?.childrenClassName)}
    >
      {children || value}
    </span>
  );

  return showTooltip ? (
    <MUITooltip
      title={
        <span
          className={cn('font-proxima-nova tpg-c2', classNames?.titleClassName)}
        >
          {value}
        </span>
      }
      placement={placement ?? 'bottom'}
      classes={{
        tooltip: cn('tooltip', classNames?.tooltipClassName),
      }}
      enterDelay={400}
      enterNextDelay={400}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [offsetX ?? 0, offsetY ?? 0],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            height: 'fit-content',
            padding: '4px 8px',
            bgcolor: '#171925',
            color: '#e3e6f1',
            margin: '2px',
            marginTop: '2px !important',
          },
        },
      }}
    >
      {clonedChildren}
    </MUITooltip>
  ) : (
    clonedChildren
  );
};
